import { ReloadOutlined } from '@ant-design/icons'
import { Vertical } from '@gravitate-js/excalibrr'
import { Button, Result } from 'antd'
import React, { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children?: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo)
    this.setState({
      error,
      errorInfo,
    })
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
    window.location.reload()
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <Vertical
          bg='layout'
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '24px',
          }}
        >
          <Result
            status='error'
            title='Something went wrong'
            subTitle={
              <>
                <p>
                  An unexpected error occurred. Please try refreshing the page.
                </p>
                {process.env.NODE_ENV === 'development' && this.state.error && (
                  <details style={{ textAlign: 'left', marginTop: 16 }}>
                    <summary style={{ cursor: 'pointer' }}>
                      Error details
                    </summary>
                    <pre
                      style={{
                        padding: 12,
                        borderRadius: 4,
                        overflow: 'auto',
                        maxHeight: 300,
                        fontSize: 12,
                        marginTop: 8,
                      }}
                    >
                      {this.state.error.toString()}
                      {this.state.errorInfo &&
                        this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </>
            }
            extra={[
              <Button
                type='primary'
                key='refresh'
                icon={<ReloadOutlined />}
                onClick={this.handleReset}
              >
                Refresh Page
              </Button>,
              <Button
                key='home'
                onClick={() =>
                  (window.location.href = '/integration-management/overview')
                }
              >
                Go Home
              </Button>,
            ]}
          />
        </Vertical>
      )
    }

    return this.props.children
  }
}
